import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { Field, useAction } from '../components/ui';
import { LegalLinks } from './Legal';
import { useInstanceMode } from '../lib/instance';

function Brand() {
  return (
    <div className="row" style={{ marginBottom: 16 }}>
      <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true"><rect width="64" height="64" rx="14" fill="#0f3d3e" /><path d="M44.7 19.3A18 18 0 1 0 44.7 44.7" fill="none" stroke="#7fe0c4" strokeWidth="6" strokeLinecap="round" /><path d="M25 32.5l5.5 5.5 11-11" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      <div><strong>Conforme</strong><div className="muted" style={{ fontSize: 12.5 }}>Cuentas a pagar y factura electrónica</div></div>
    </div>
  );
}

export function Login() {
  const s = useSession();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await run(() => api.post('/auth/login', { email, password }));
    if (r) { await s.refresh(); nav(r.mfaRequired ? '/verificar' : '/'); }
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Brand />
        <h1>Entrar</h1>
        <Field label="Correo"><input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Contraseña"><input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <button className="primary" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</button>
        <p className="muted"><Link to="/recuperar">¿Has olvidado tu contraseña?</Link></p>
        <p className="muted">¿Primera vez? <Link to="/alta">Crea tu cuenta</Link></p>
        <LegalLinks />
      </form>
    </div>
  );
}

/** Pide el enlace de recuperación. La respuesta es la misma exista o no el correo. */
export function ForgotPassword() {
  const { busy, run } = useAction();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => api.post('/auth/password/forgot', { email }))) setSent(true);
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Brand />
        <h1>Recuperar contraseña</h1>
        {sent ? (
          <div className="alert ok">Si hay una cuenta con ese correo, te hemos enviado un enlace para elegir una contraseña nueva. Caduca en 1 hora. Revisa también la carpeta de correo no deseado.</div>
        ) : (
          <>
            <p className="muted">Escribe tu correo y te enviaremos un enlace para elegir una contraseña nueva.</p>
            <Field label="Correo"><input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
            <button className="primary" disabled={busy}>Enviar enlace</button>
          </>
        )}
        <p className="muted"><Link to="/entrar">Volver a entrar</Link></p>
      </form>
    </div>
  );
}

/** Elige la contraseña nueva desde el enlace del correo (/recuperar/:token). */
export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => api.post('/auth/password/reset', { token, password }), 'Contraseña cambiada. Ya puedes entrar.')) nav('/entrar');
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Brand />
        <h1>Nueva contraseña</h1>
        <Field label="Contraseña nueva" hint="Mínimo 10 caracteres. Al cambiarla se cerrarán tus sesiones abiertas."><input type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} autoFocus /></Field>
        <Field label="Repite la contraseña"><input type="password" autoComplete="new-password" minLength={10} required value={repeat} onChange={(e) => setRepeat(e.target.value)} /></Field>
        {repeat && repeat !== password && <p className="hint">Las contraseñas no coinciden.</p>}
        <button className="primary" disabled={busy || password !== repeat}>Guardar contraseña</button>
        <p className="muted"><Link to="/recuperar">Pedir otro enlace</Link></p>
      </form>
    </div>
  );
}

export function Signup() {
  const s = useSession();
  const modo = useInstanceMode();
  const [inviteCode, setInviteCode] = useState('');
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [f, setF] = useState({ organizationName: '', kind: 'pyme', companyName: '', companyNif: '', name: '', email: '', password: '' });
  const [acceptLegal, setAcceptLegal] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Una empresa sola no ve el campo de organización: se llama como la empresa.
    const body = { ...f, organizationName: f.kind === 'pyme' || !f.organizationName.trim() ? f.companyName : f.organizationName, acceptLegal, ...(modo === 'prueba' ? { inviteCode } : {}) };
    const r = await run(() => api.post('/auth/signup', body));
    if (r) { await s.refresh(); nav('/ajustes?bienvenida=1'); }
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit} style={{ width: 'min(520px, 100%)' }}>
        <Brand />
        <h1>{modo === 'prueba' ? 'Crea tu cuenta de prueba' : 'Crea tu cuenta'}</h1>
        {modo === 'prueba' && <div className="alert info">Esto es el entorno de prueba: los datos se eliminan a los 7 días y no hay copia de seguridad. El alta es por invitación.</div>}
        {modo === 'prueba' && <Field label="Código de invitación"><input required value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} autoComplete="off" /></Field>}
        <Field label="Tipo"><select value={f.kind} onChange={set('kind')}><option value="pyme">Empresa</option><option value="asesoria">Asesoría o gestoría (varias empresas cliente)</option><option value="grupo">Grupo de empresas</option></select></Field>
        <div className="grid2">
          <Field label="Razón social de la empresa"><input required value={f.companyName} onChange={set('companyName')} /></Field>
          <Field label="NIF de la empresa"><input required value={f.companyNif} onChange={set('companyNif')} /></Field>
        </div>
        {f.kind !== 'pyme' && <Field label="Nombre de la organización"><input required value={f.organizationName} onChange={set('organizationName')} /></Field>}
        <div className="grid2">
          <Field label="Tu nombre"><input required value={f.name} onChange={set('name')} /></Field>
          <Field label="Tu correo"><input type="email" required value={f.email} onChange={set('email')} /></Field>
        </div>
        <Field label="Contraseña" hint="Mínimo 10 caracteres."><input type="password" minLength={10} required value={f.password} onChange={set('password')} /></Field>
        <label className="check"><input type="checkbox" required checked={acceptLegal} onChange={(e) => setAcceptLegal(e.target.checked)} />
          <span>He leído y acepto las <Link to={`/legal/condiciones${modo === 'prueba' ? '-prueba' : ''}`} target="_blank">condiciones de uso</Link> y el <Link to={`/legal/encargado${modo === 'prueba' ? '-prueba' : ''}`} target="_blank">contrato de encargado del tratamiento</Link> (RGPD), y he leído la <Link to={`/legal/privacidad${modo === 'prueba' ? '-prueba' : ''}`} target="_blank">política de privacidad</Link>.</span></label>
        <button className="primary" disabled={busy || !acceptLegal}>Crear cuenta</button>
        <p className="muted">¿Ya tienes cuenta? <Link to="/entrar">Entrar</Link></p>
        <LegalLinks />
      </form>
    </div>
  );
}

export function MfaVerify() {
  const s = useSession();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [code, setCode] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => api.post('/auth/mfa/verify', { code }))) { await s.refresh(); nav('/'); }
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Brand />
        <h1>Segundo factor</h1>
        <p className="muted">Introduce el código de 6 dígitos de tu aplicación de autenticación.</p>
        <Field label="Código"><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus /></Field>
        <button className="primary" disabled={busy || code.length !== 6}>Verificar</button>
        <button type="button" className="ghost" onClick={async () => { await s.logout(); nav('/entrar'); }}>Salir</button>
      </form>
    </div>
  );
}
